import { useState, useEffect, useRef } from "react";
import { toast } from 'react-hot-toast';
import {
  Search,
  MoreVertical,
  Edit,
  Trash2,
  UserPlus,
  FileText,
  AlertCircle,
  Filter,
  Loader2,
  AlertTriangle
} from "lucide-react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import api, { errorMessage, getAllPages, logError } from "../services/api";
import { useRiderUpdates } from "../services/adminSocket";
import { isSuperAdmin } from "../utils/role";
import RefreshButton from "../components/RefreshButton";
import RiderStatusBadge from "../components/RiderStatusBadge";
import { DISPATCHABLE } from "../utils/riderStatus";
import { formatLagosDate } from "../utils/tripLog";

const PAGE_SIZE = 20;

// Status options for filtering
const statusOptions = [
  { value: 'all', label: 'All Drivers' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' }
];

const Drivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusSending, setStatusSending] = useState(false);
  const statusInFlight = useRef(false);
  const [page, setPage] = useState(1);
  // Permanent account deletion is a super-admin action.
  const canDelete = isSuperAdmin();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle delete driver
  const handleDeleteClick = (driver) => {
    setDriverToDelete(driver);
    setShowDeleteModal(true);
  };

  // Cancel delete
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDriverToDelete(null);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!driverToDelete) return;

    // The account is deleted by its user id (DELETE /admin/users/:userId).
    const userId = driverToDelete.user?._id;
    if (!userId) {
      toast.error('This rider has no account id, so it cannot be deleted here.');
      return;
    }
    const loadingToast = toast.loading('Deleting rider...');
    try {
      setDeletingId(driverToDelete.id);
      const res = await api.delete(`/v1/admin/users/${userId}`);
      await fetchDrivers();
      setShowDeleteModal(false);
      setDriverToDelete(null);
      toast.success(res.data?.message || 'Rider account deleted', { id: loadingToast });
    } catch (error) {
      logError('Error deleting driver:', error);
      toast.error(errorMessage(error, 'Failed to delete rider'), { id: loadingToast });
    } finally {
      setDeletingId(null);
    }
  };

  // Cancel status change
  const cancelStatusChange = () => {
    setShowStatusModal(false);
    setDriverToDelete(null);
    setNewStatus('');
  };

  // Get status display text
  const getStatusDisplayText = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Update driver status
  const updateDriverStatus = async (driverId, status) => {
    // One request per decision, however many times the button is pressed.
    if (!driverId || !status || statusInFlight.current) return;
    statusInFlight.current = true;
    setStatusSending(true);
    const loadingToast = toast.loading(`Updating rider to ${status}...`);

    try {

      // Use the PUT endpoint specifically for status updates (as per existing logic in handleStatusChange)
      await api.put(`/v1/admin/riders/${driverId}/status`, {
        status: status
      });

      // Update the UI state
      setDrivers(prevDrivers => 
        prevDrivers.map(driver => 
          driver.id === driverId ? { ...driver, status } : driver
        )
      );

      // Refresh the drivers list to ensure consistency
      await fetchDrivers();

      toast.success(`Rider ${status} successfully`, { id: loadingToast });
    } catch (error) {
      logError('Error updating driver status:', error);
      // Replaces the loading toast, so it never spins on after a failure.
      toast.error(errorMessage(error, 'Failed to update driver status'), { id: loadingToast });
      // Refresh to revert any optimistic UI changes
      await fetchDrivers();
    } finally {
      statusInFlight.current = false;
      setStatusSending(false);
      setShowStatusModal(false);
      setDriverToDelete(null);
      setNewStatus('');
    }
  };

  // Fetch drivers from API
  const fetchDrivers = async () => {
    try {
      setLoading(true);
      setError(null);

      // The riders endpoint rejects ?status= (400), so load every rider and
      // filter here. 13 riders today; a server-side filter is the long-term fix.
      const allRiders = await getAllPages('/v1/admin/riders');
      const driversData = allRiders.map(driver => {
          const id = driver.id || driver._id || driver.user?._id;
          return {
            ...driver, // Spread first to avoid overwriting our mapped fields
            id,
            _id: driver._id || id,
            name: driver.user?.name || 'N/A',
            email: driver.user?.email || 'N/A',
            phone: driver.user?.phone || 'N/A',
            status: driver.status || 'pending',
            nin: driver.nin || 'N/A',
            plateNumber: driver.plateNumber || 'N/A',
            documents: {
              driverLicenseUrl: driver.documents?.driverLicenseUrl || '',
              passportPhotoUrl: driver.documents?.passportPhotoUrl || ''
            },
            // The real profile status; null when the rider has no profile yet.
            profileStatus: driver.status || null,
            // Who can take a ride now; the server already sorts free riders first.
            dispatchStatus: driver.dispatchStatus || null,
            alwaysOnline: !!driver.alwaysOnline,
            createdAt: driver.createdAt || null,
            updatedAt: driver.updatedAt || null
          };
        });

      setDrivers(driversData);
      setUpdatedAt(new Date());
    } catch (err) {
      logError('Error fetching drivers:', err);
      // Keep the riders already on screen; the banner says the refresh failed.
      setError(`Failed to load riders: ${errorMessage(err, err.message)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  useRiderUpdates(fetchDrivers);

  // Filter by profile status and search over all riders, then page.
  const filteredDrivers = Array.isArray(drivers) ? drivers.filter(driver => {
    if (!driver) return false;
    if (statusFilter !== 'all' && driver.profileStatus !== statusFilter) return false;
    const searchLower = searchTerm.toLowerCase();
    return (
      (driver.name?.toLowerCase() || '').includes(searchLower) ||
      (driver.driverId?.toLowerCase() || '').includes(searchLower) ||
      (driver.plateNumber?.toLowerCase() || '').includes(searchLower)
    );
  }) : [];

  const totalPages = Math.max(1, Math.ceil(filteredDrivers.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageDrivers = filteredDrivers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  // Show status change confirmation
  const showStatusConfirmation = (driverId, newStatus) => {
    const driver = drivers.find(d => d.id === driverId);
    if (!driver) return;

    setDriverToDelete(driver); // Reusing this state to store the driver
    setNewStatus(newStatus);
    setShowStatusModal(true);
  };

  // Handle view driver
  const handleViewDriver = (driver) => {
    // Save the current driver to localStorage before navigating
    const drivers = JSON.parse(localStorage.getItem("drivers") || "[]");
    const existingDriverIndex = drivers.findIndex(d => d.id === driver.id || d._id === driver.id);

    // I should Format the driver data to match the expected structure in DriverDetails
    const driverToSave = {
      id: driver.id,
      _id: driver.id, // For backward compatibility
      name: driver.user?.name,
      email: driver.user?.email,
      phone: driver.user?.phone,
      status: driver.status,
      plateNumber: driver.plateNumber,
      documents: driver.documents || {},
      user: driver.user || {},
      ...driver // Include all other driver properties
    };

    if (existingDriverIndex >= 0) {
      // Update existing driver
      drivers[existingDriverIndex] = driverToSave;
    } else {
      // Add new driver
      drivers.push(driverToSave);
    }

    localStorage.setItem("drivers", JSON.stringify(drivers));
    navigate(`/driver-details/${driver.id}`);
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    }
  };

  if (loading && drivers.length === 0) {
    return (
      <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center text-gray-900 dark:text-gray-100">
          <Loader2 className="h-8 w-8 animate-spin text-yellow-500" />
          <span className="ml-2">Loading drivers...</span>
        </div>
      </div>
    );
  }

  if (error && drivers.length === 0) {
    return (
      <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex flex-col items-center justify-center p-4 text-gray-900 dark:text-gray-100">
          <AlertTriangle className="h-12 w-12 text-red-500 mb-4" />
          <p className="text-red-500 dark:text-red-400 text-lg mb-4">{error}</p>
          <button
            onClick={fetchDrivers}
            className="px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <Sidebar activeItem="drivers" />
      <div className="lg:pl-64">
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm transition-colors duration-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Riders Management
              </h1>

              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    className="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg pl-4 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>

                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search riders..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <RefreshButton onRefresh={fetchDrivers} loading={loading} updatedAt={updatedAt} />
              <button
                onClick={() => navigate("/drivers/add")}
                className="flex items-center gap-2 px-4 py-2 bg-[#FF8C00] text-black rounded-lg hover:bg-[#E67E22] transition"
              >
                <UserPlus size={18} />
                Add New Rider
              </button>
            </div>
          </div>

          {error ? (
            <div role="alert" className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">
              <span>{error}. Showing the last loaded riders.</span>
              <button type="button" onClick={fetchDrivers} className="font-medium underline">
                Retry
              </button>
            </div>
          ) : null}

          {/* Search and Filter */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden transition-colors duration-200">
            {/* Table content will go here */}
          </div>

          {/* Status Change Confirmation Modal */}
          {showStatusModal && driverToDelete && (
            <div className="fixed inset-0 backdrop-blur-sm bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {newStatus === 'approved' ? 'Approve' : 'Reject'} Rider
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Are you sure you want to {newStatus} <span className="font-medium">{driverToDelete.name}</span>?
                  {newStatus === 'rejected' ? ' This will notify the driver that their application has been rejected.' : ''}
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={cancelStatusChange}
                    disabled={statusSending}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => updateDriverStatus(driverToDelete.id, newStatus)}
                    disabled={statusSending}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-wait ${newStatus === 'approved'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-red-600 hover:bg-red-700'
                      }`}
                  >
                    {statusSending ? 'Sending…' : newStatus === 'approved' ? 'Approve' : 'Reject'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal && driverToDelete && (
            <div className="fixed inset-0  bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="bg-white dark:bg-black rounded-xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Delete Rider
                </h3>
                <div className="text-gray-600 mb-6 space-y-2 text-sm">
                  <p>
                    This <span className="font-semibold">permanently deletes</span>{' '}
                    {driverToDelete.name}&apos;s account. It cannot be undone.
                  </p>
                  <p>
                    Their wallet must be <span className="font-semibold">₦0</span>{' '}
                    first. If they still hold money, the delete is refused: pay
                    it out before deleting.
                  </p>
                  <p>Any ride they are on is cancelled. Their payment records are kept.</p>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={cancelDelete}
                    disabled={deletingId === driverToDelete.id}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    disabled={deletingId === driverToDelete.id}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {deletingId === driverToDelete.id ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Deleting...
                      </>
                    ) : (
                      "Delete"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Drivers Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            {filteredDrivers.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No riders found</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {statusFilter === 'all'
                    ? 'There are no riders in the system.'
                    : `No ${statusFilter} riders found.`}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-4 font-medium text-gray-700 tracking-wider text-left">
                        Rider Name
                      </th>
                      <th className="px-3 py-4 font-medium text-gray-700 tracking-wider text-left">
                        Rider ID
                      </th>
                      <th className="px-3 py-4 font-medium text-gray-700 tracking-wider text-left">
                        License
                      </th>
                      <th className="px-3 py-4 font-medium text-gray-700 tracking-wider text-left">
                        Plate Number
                      </th>
                      <th className="px-3 py-4 font-medium text-gray-700 tracking-wider text-left">
                        Status
                      </th>
                      <th className="px-3 py-4 font-medium text-gray-700 tracking-wider text-left">
                        Date
                      </th>
                      <th className="px-3 py-4 font-medium text-gray-700 tracking-wider text-left">
                        Request
                      </th>
                      <th className="px-3 py-4 font-medium text-gray-700 tracking-wider text-left">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-black divide-y divide-gray-200">
                    {pageDrivers.map((driver, index) => (
                      <tr
                        key={driver?.id || `driver-${index}`}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                        onClick={(e) => {
                          // Don't navigate if clicking on action buttons or links
                          if (!e.target.closest('button, a, [role="button"]')) {
                            navigate(`/driver-details/${driver.id}`);
                          }
                        }}
                      >
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            {driver.profileImage ? (
                              <img
                                className="h-10 w-10 rounded-full object-cover"
                                src={driver.profileImage}
                                alt={driver.name}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(driver.name || '') + '&background=random';
                                }}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-300">
                                {driver.name ? driver.name.charAt(0).toUpperCase() : 'U'}
                              </div>
                            )}
                            <div className="min-w-0">
                              <span className="block text-gray-900 dark:text-white font-medium hover:text-[#FF8C00] hover:underline transition">
                                {driver.name || 'N/A'}
                              </span>
                              {DISPATCHABLE.has(driver.dispatchStatus) ? (
                                <span className="mt-0.5 block">
                                  <RiderStatusBadge status={driver.dispatchStatus} alwaysOnline={driver.alwaysOnline} />
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td
                          className="px-3 py-4 whitespace-nowrap text-gray-900"
                          title={driver.id || driver._id || ''}
                        >
                          <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                            {(driver.id || driver._id)
                              ? `…${String(driver.id || driver._id).slice(-6)}`
                              : 'N/A'}
                          </span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          {driver.documents?.driverLicenseUrl ? (
                            <a
                              href={driver.documents.driverLicenseUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                              title="View Driver's License"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V7.414L10.586 4H6z" clipRule="evenodd" />
                              </svg>
                            </a>
                          ) : (
                            <span className="text-gray-400">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V7.414L10.586 4H6z" clipRule="evenodd" />
                              </svg>
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-gray-900">
                          {driver.plateNumber || 'N/A'}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${!driver.hasProfile
                                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                                : getStatusBadgeClass(driver.status?.toLowerCase())
                              }`}
                          >
                            {!driver.hasProfile ? 'Incomplete' : (driver.status ?
                              driver.status.charAt(0).toUpperCase() + driver.status.slice(1).toLowerCase() :
                              'N/A')}
                          </span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-gray-900">
                          {formatLagosDate(driver.createdAt, 'en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {/* Approve Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (driver.hasProfile && driver.status?.toLowerCase() !== 'approved') {
                                  showStatusConfirmation(driver.id, 'approved');
                                }
                              }}
                              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${!driver.hasProfile
                                  ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                                  : driver.status?.toLowerCase() === 'approved'
                                    ? 'bg-opacity-50 bg-green-500 dark:bg-green-800 text-white cursor-not-allowed opacity-70'
                                    : 'bg-green-600 hover:bg-green-700 text-white hover:shadow-md'
                                }`}
                              title={!driver.hasProfile
                                ? 'Rider has not completed application'
                                : driver.status?.toLowerCase() === 'approved'
                                  ? 'Already approved'
                                  : 'Approve driver'
                              }
                              disabled={!driver.hasProfile || driver.status?.toLowerCase() === 'approved'}
                            >
                              {driver.status?.toLowerCase() === 'approved' ? 'Approved' : 'Approve'}
                            </button>

                            {/* Reject Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (driver.hasProfile && driver.status?.toLowerCase() !== 'rejected') {
                                  showStatusConfirmation(driver.id, 'rejected');
                                }
                              }}
                              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${!driver.hasProfile
                                  ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                                  : driver.status?.toLowerCase() === 'rejected'
                                    ? 'bg-opacity-50 bg-red-500 dark:bg-red-900 text-white cursor-not-allowed opacity-70'
                                    : 'bg-red-600 hover:bg-red-700 text-white hover:shadow-md'
                                }`}
                              title={!driver.hasProfile
                                ? 'Rider has not completed application'
                                : driver.status?.toLowerCase() === 'rejected'
                                  ? 'Already rejected'
                                  : 'Reject driver'
                              }
                              disabled={!driver.hasProfile || driver.status?.toLowerCase() === 'rejected'}
                            >
                              {driver.status?.toLowerCase() === 'rejected' ? 'Rejected' : 'Reject'}
                            </button>
                          </div>
                          {!driver.hasProfile ? (
                            <p className="mt-1 text-[11px] text-gray-500">Application not finished</p>
                          ) : null}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            {/* View Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // Use User ID for navigation (backend expects user._id, not rider profile id)
                                const userId = driver.user?._id || driver.id;
                                navigate(`/driver-details/${userId}`);
                              }}
                              className="p-1.5 text-gray-500 hover:text-yellow-600 transition-colors"
                              title="View Details"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                              </svg>
                            </button>

                            {/* Edit Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // Edit uses the RiderProfile id (GET/PUT /admin/riders/:id/profile).
                                navigate(`/drivers/edit/${driver.id || driver._id}`);
                              }}
                              className="p-1.5 text-gray-500 hover:text-blue-600 transition-colors"
                              title="Edit Rider"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                            </button>

                            {/* Delete Button */}
                            {canDelete && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(driver);
                              }}
                              className="p-1.5 text-gray-500 hover:text-red-600 transition-colors"
                              title="Delete Rider"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {totalPages > 1 && (
                  <nav className="flex items-center justify-between pt-4 text-sm text-gray-600 dark:text-gray-300" aria-label="Rider pages">
                    <span>
                      Page {currentPage} of {totalPages} · {filteredDrivers.length} riders
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setPage(currentPage - 1)}
                        disabled={currentPage <= 1}
                        className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 disabled:opacity-40"
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        onClick={() => setPage(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  </nav>
                )}
              </div>
            )}
          </div>

          {/* Status Change Modal Removed (Duplicate) */}

          {/* Add Outlet for nested routes */}
          <Outlet />

        </main>
      </div>
    </div>
  );
};

export default Drivers;
